import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface JuniorCoach {
  id: string;
  coach_id_code: string;
  first_name: string;
  last_name: string;
  email: string;
  assigned_sports: string[];
  coach_level?: string;
  academy_id?: string;
  created_at: string;
  updated_at: string;
}

interface CoachState {
  juniorCoaches: JuniorCoach[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setJuniorCoaches: (coaches: JuniorCoach[]) => void;
  addJuniorCoach: (coach: JuniorCoach) => void;
  updateJuniorCoach: (id: string, updates: Partial<JuniorCoach>) => void;
  deleteJuniorCoach: (id: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

/**
 * Coach management state store using Zustand
 * This ensures consistent state across Dashboard and Management pages
 */
export const useCoachStore = create<CoachState>()(
  persist(
    (set, get) => ({
      juniorCoaches: [],
      isLoading: false,
      error: null,

      setJuniorCoaches: (coaches) => {
        set({ juniorCoaches: coaches, error: null });
      },

      addJuniorCoach: (coach) => {
        const { juniorCoaches } = get();
        set({ 
          juniorCoaches: [...juniorCoaches, coach],
          error: null 
        });
      },

      updateJuniorCoach: (id, updates) => {
        const { juniorCoaches } = get();
        const updatedCoaches = juniorCoaches.map(coach =>
          coach.id === id ? { ...coach, ...updates, updated_at: new Date().toISOString() } : coach
        );
        set({ 
          juniorCoaches: updatedCoaches,
          error: null 
        });
      },

      deleteJuniorCoach: (id) => {
        const { juniorCoaches } = get();
        set({ 
          juniorCoaches: juniorCoaches.filter(coach => coach.id !== id),
          error: null 
        });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'coach-storage',
      partialize: (state) => ({
        juniorCoaches: state.juniorCoaches,
      }),
    }
  )
);
